import Cookie from '@wetransfer/concorde-cookie';
import get from 'lodash/get';

import { actions as notificationActions } from './notification';

import ActionHelper from '@module/redux-actions';

import {
  MAX_MESSAGE_SIZE_IN_BYTES,
  REGEXP_BLANK,
} from '@app/transfer-bundle/config/transfer-config';
import { extractEmails } from '@app/transfer-bundle/helpers/extract-emails';
import Helper from '@app/helper';
import { TRANSFER_TYPES } from '@app/transfer-bundle/constant/transfer-types';
import { AppConfig } from '@app/core-bundle/config/app-config';
import {
  COOKIE_TRANSFER_TYPE_LINK,
  COOKIE_TRANSFER_TYPE_EMAIL,
} from '@app/constant/cookie-transfer-types';
import { isLoggedInPlusUser, isLoggedInFreeUser } from '@app/helper/user';
import { isSendingToDefaultRecipientSelector } from '@app/store/selector/transfers';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';
import {
  FF_REMOVE_TRANSFER_DEFAULT_EXPIRY,
  FF_FORCE_ACCOUNT_VERIFICATION,
} from '@app/constant/experiments';
import LaunchDarkly from '@module/launchdarkly';

export const actions = ActionHelper.types([
  'TRANSFER_INIT',
  'TRANSFER_CREATE',
  'TRANSFER_CHANGE_TYPE',
  'TRANSFER_CHANGE_EXPIRY',
  'TRANSFER_CHANGE_PASSWORD',
  'TRANSFER_SET_SENDER',
  'TRANSFER_SET_MESSAGE',
  'TRANSFER_SET_LANGUAGE',
  'TRANSFER_SET_DOMAIN_USER_ID',
  'TRANSFER_SET_CHANNEL',
  'TRANSFER_ADD_RECIPIENT',
  'TRANSFER_ADD_FILES',
  'TRANSFER_REMOVE_FILE',
  'TRANSFER_REMOVE_DIRECTORY',
  'TRANSFER_REMOVE_RECIPIENT',
  'TRANSFER_RESET',
  'TRANSFER_START',
  'TRANSFER_STATE_DOWNLOADABLE',
  'TRANSFER_STATE_DOWNLOAD_STARTING',
  'TRANSFER_STATE_DOWNLOADING',
  'TRANSFER_STATE_FINISHED',
  'TRANSFER_RESET_DOWNLOAD',
  'TRANSFER_SET_STATE',
  'TRANSFER_SET_EXPIRY_IN_SECONDS',
  'TRANSFER_SET_SHORT_URL',
  'TRANSFER_SET_ID',
  'TRANSFER_SET_DISPLAY_NAME',
  'TRANSFER_SET_SECURITY_HASH',
  'TRANSFER_SET_UPLOAD_STARTED_AT',

  // Download related
  'DOWNLOAD_PENDING',
  'DOWNLOAD_SUCCESS',
  'DOWNLOAD_ERROR',
  'TRANSFER_DOWNLOAD_EXPIRED',
  'TRANSFER_DOWNLOAD_INVALID',
  'TRANSFER_STATE_DOWNLOAD_STARTING_FILE',
  'TRANSFER_STATE_DOWNLOADING_FILE',
  'TRANSFER_STATE_FINISHED_FILE',
  'TRANSFER_RESET_VERIFICATION_REQUIRED',
  'SHOW_TRANSFER_OPTIONS_TOOLTIP',

  'RECAPTCHA_CHALLENGE_RECEIVED',

  // Upload related
  'UPLOAD_PENDING',
  'UPLOAD_RESET',
  'UPLOAD_ERROR',
  'UPLOAD_ERROR_RESET',
  'UPLOAD_NEED_VERIFICATION',
  'UPLOAD_VERIFIED',
  'UPLOAD_DONE',

  // Preview related
  'PREVIEW_LOADING',
  'PREVIEW_LOADED',
  'PREVIEW_ERROR',
]);

function getDefaultRecipient(user, account, channel) {
  const channelName = channel.name || '';
  const channelEmail = channel.defaultRecipient || false;

  // When a logged in Pro user is on another Pro user's channel, which has a default recipient set,
  // then use this as default recipient.
  if (user.loggedIn && channelName !== account.subdomain_name && channelEmail) {
    return channelEmail;
  }
  // User is not logged in but a channel email is available
  if (!user.loggedIn && channelEmail) {
    return channelEmail;
  }

  return false;
}

export const getUserType = ({ user, channel, transferFromState }) => {
  if (isLoggedInPlusUser(user)) {
    return 'plus';
  }

  if (
    isSendingToDefaultRecipientSelector({
      transfer: transferFromState,
      channel,
    })
  ) {
    return 'plus';
  }

  if (isLoggedInFreeUser(user)) {
    return 'freeAccount';
  }

  return 'free';
};

export function dispatchNotification(dispatch, transfer, channel, text) {
  const ignoredErrorMessages = [
    'LimitReached',
    'UserBlocked',
    'TransferFailed',
    'UnavailableParent',
    'TransferCanceled',
    'HumanVerificationRequired',
  ];
  if (ignoredErrorMessages.indexOf(text) !== -1) {
    return;
  }

  // NOTE: This is for the 20gb channel feature
  if (text === 'TransferTooBig') {
    const containsRecipient =
      transfer.recipients.length === 1 &&
      transfer.recipients[0] === channel.defaultRecipient;
    if (channel.defaultRecipient && !containsRecipient) {
      text = 'RequiresDefaultRecipient';
    }
  }

  // Dispatch the notification
  dispatch({ type: notificationActions.NOTIFICATION_TRANSFER_SET, text });
}

export default ActionHelper.generate({
  changeType: [actions.TRANSFER_CHANGE_TYPE, 'transferType'],
  changeExpiry: [actions.TRANSFER_CHANGE_EXPIRY, 'period'],
  changePassword: [actions.TRANSFER_CHANGE_PASSWORD, 'password'],
  removeFile: [actions.TRANSFER_REMOVE_FILE, 'fileName'],
  removeDirectory: [actions.TRANSFER_REMOVE_DIRECTORY, 'directoryName'],
  removeRecipient: [actions.TRANSFER_REMOVE_RECIPIENT, 'email'],
  clearUploadError: [actions.UPLOAD_ERROR_RESET],
  displayError: [actions.UPLOAD_ERROR, 'error', 'field'],
  resetDownload: [actions.TRANSFER_RESET_DOWNLOAD],
  setDownloadable: [actions.TRANSFER_STATE_DOWNLOADABLE, 'transferId', 'items'],
  previewLoading: [actions.PREVIEW_LOADING, 'file_id', 'file_url'],
  previewLoaded: [actions.PREVIEW_LOADED, 'file_id'],
  previewError: [actions.PREVIEW_ERROR, 'file_id'],
  recaptcha: [actions.RECAPTCHA_CHALLENGE_RECEIVED, 'transfer_id'],
  setState: [actions.TRANSFER_SET_STATE, 'state'],
  resetVerificationRequired: [actions.TRANSFER_RESET_VERIFICATION_REQUIRED],
  showTransferOptionsTooltip: [actions.SHOW_TRANSFER_OPTIONS_TOOLTIP],

  init({
    transferType,
    userType,
    channelName,
    accountId,
    spaceId,
    expiry,
    defaultProExpiry,
    defaultFreeExpiry,
    recipients,
    sender,
    message,
    parentId,
  }) {
    return {
      type: actions.TRANSFER_INIT,
      transferType,
      userType,
      expiry,
      defaultProExpiry,
      defaultFreeExpiry,
      recipients,
      sender,
      channelName,
      accountId,
      spaceId,
      message,
      parentId,
    };
  },

  create() {
    return (dispatch, getState) => {
      const { user, channel, account, transfer, route } = getState();

      // Get the last used sender from a cookie
      const defaultSender = Helper.getLastUsedSender();
      const defaultRecipient = getDefaultRecipient(user, account, channel);

      // Get the last used type from a cookie
      let defaultType = Cookie.get(AppConfig.cookies.transferType.name);
      if (defaultType === COOKIE_TRANSFER_TYPE_LINK) {
        defaultType = TRANSFER_TYPES.LINK;
      }
      if (defaultType === '3') {
        defaultType = TRANSFER_TYPES.EMAIL;
      }
      if (defaultType === '2') {
        defaultType = TRANSFER_TYPES.EMAIL;
      }
      if (defaultType === COOKIE_TRANSFER_TYPE_EMAIL) {
        defaultType = TRANSFER_TYPES.EMAIL;
      }

      // We only want to allow email transfers when the default recipient is locked in profile pages
      if (
        Helper.isDefaultRecipientInUploadLocked({
          channelDefaultRecipient: channel.defaultRecipient,
          channelProfileReceivingLocked: channel.profileReceivingLocked,
          userLoggedIn: user.loggedIn,
          channelName: channel.name,
          accountSubDomainName: account.subdomain_name,
        })
      ) {
        defaultType = TRANSFER_TYPES.EMAIL;
      }

      const isFreeAccountUserOnChannel =
        isLoggedInFreeUser(user) && channel && channel.name;
      let expiry;
      if (
        (LaunchDarkly.getVariant(FF_REMOVE_TRANSFER_DEFAULT_EXPIRY) &&
          isLoggedInPlusUser(user)) ||
        isFreeAccountUserOnChannel
      ) {
        expiry = user.rules.defaultProExpiry;
      } else {
        expiry = get(user, 'preferences.transfer_default_expiry_string', null);
      }

      const shouldForceVerification =
        LaunchDarkly.getVariant(FF_FORCE_ACCOUNT_VERIFICATION) &&
        user.hasSkippedVerification &&
        route.path === '/account-verification' &&
        !!route.query.sig;

      const shouldVerifyAccount =
        user.loggedIn && (!user.isVerified || shouldForceVerification);

      const isForceVerificationEnabled =
        user.loggedIn && user.forceVerificationEnabled;

      dispatch({
        type: actions.TRANSFER_INIT,
        userType: getUserType({
          user,
          channel,
          transferFromState: transfer,
        }),
        transferType:
          defaultType === TRANSFER_TYPES.LINK
            ? TRANSFER_TYPES.LINK
            : TRANSFER_TYPES.EMAIL,
        accountId: account.id,
        channelName: channel.name,
        expiry,
        defaultProExpiry: user.rules.defaultProExpiry,
        defaultFreeExpiry: user.rules.defaultFreeExpiry,
        recipients: defaultRecipient ? [defaultRecipient] : [],
        sender: defaultSender,
        state: isForceVerificationEnabled
          ? TRANSFER_STATES.FORCE_ACCOUNT_VERIFICATION
          : shouldVerifyAccount
          ? TRANSFER_STATES.VERIFY_ACCOUNT
          : null,
      });
    };
  },

  createSimilar() {
    return (dispatch, getState) => {
      const { transfer, user, account, channel } = getState();
      dispatch({
        type: actions.TRANSFER_INIT,
        userType: getUserType({
          user: user,
          channel: channel,
          transferFromState: transfer,
        }),
        transferType: transfer.type,
        accountId: account.id,
        channelName: transfer.channel,
        expiry:
          LaunchDarkly.getVariant(FF_REMOVE_TRANSFER_DEFAULT_EXPIRY) &&
          isLoggedInPlusUser(user)
            ? user.rules.defaultProExpiry
            : transfer.expiry,
        defaultProExpiry: user.rules.defaultProExpiry,
        defaultFreeExpiry: user.rules.defaultFreeExpiry,
        recipients: Helper.isDefaultRecipientInUploadLocked({
          channelDefaultRecipient: channel.defaultRecipient,
          channelProfileReceivingLocked: channel.profileReceivingLocked,
          userLoggedIn: user.loggedIn,
          channelName: channel.name,
          accountSubDomainName: account.subdomain_name,
        })
          ? transfer.recipients
          : [],
        sender: transfer.sender,
      });
    };
  },

  // Verify the download
  verifiedPassword(password) {
    return (dispatch) => {
      dispatch({ type: actions.TRANSFER_CHANGE_PASSWORD, password });
    };
  },

  // Validate and set the Transfer sender
  setSender(email) {
    return (dispatch, getState) => {
      if (!email.replace(REGEXP_BLANK, '')) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingSender',
        });
      } else if (!Helper.validateEmail(email)) {
        dispatch({ type: actions.TRANSFER_SET_SENDER, email: '' });
        const { notification } = getState();
        if (
          get(notification, 'transfer.text') === 'TipSenderRequireVerification'
        ) {
          return;
        }
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidSender',
        });
      }

      return dispatch({ type: actions.TRANSFER_SET_SENDER, email });
    };
  },

  // Validate and set the Transfer message
  setMessage(message) {
    const byteSize = unescape(encodeURIComponent(message)).length;

    if (byteSize > MAX_MESSAGE_SIZE_IN_BYTES) {
      return {
        type: notificationActions.NOTIFICATION_TRANSFER_SET,
        text: 'MessageTooLarge',
      };
    }

    // Check max length (bytes)
    return { type: actions.TRANSFER_SET_MESSAGE, message };
  },

  // Validate and add a Transfer recipient
  addRecipient(email) {
    return (dispatch, getState) => {
      const { transfer, user } = getState();
      const emails = extractEmails(email);

      // We only allow so many recipients for a transfer
      if (
        transfer.recipients.length + emails.length >
        user.rules.maximumNumberOfRecipients
      ) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'TooManyRecipients',
        });
      }

      // Nothing filled in
      if (!email.replace(REGEXP_BLANK, '') && emails.length === 0) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'MissingRecipients',
        });
      }

      // Doesn't match.... the regexp!
      if (!Helper.validateEmail(email) && emails.length <= 1) {
        return dispatch({
          type: notificationActions.NOTIFICATION_TRANSFER_SET,
          text: 'InvalidRecipient',
        });
      }

      return dispatch({ type: actions.TRANSFER_ADD_RECIPIENT, emails });
    };
  },
});
